import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
} from '@mui/material';
import { useBooking } from '../../../../context/bookingContext';
import { useEffect, useState } from 'react';
import useBranch from '../../../../hooks/useBranch';
import useEntertainment from '../../../../hooks/useEntertainment';
import useResource from '../../../../hooks/useResource';

// branchLocked: locks the branch select in cart mode — all reservations of a cart
// must belong to the same branch, so the branch cannot change while items are parked.
export default function EntertainmentSelect({
  branchId = null,
  entertainmentId = null,
  branchLocked = false,
}) {
  const {
    selectedBranch,
    setSelectedBranch,
    selectedEntertainment,
    setSelectedEntertainment,
    setSelectedEntertainmentObject,
    selectedProduct,
    setSelectedProduct,
    selectedResource,
    setSelectedResource,
    entertainmentError,
    setEntertainmentError,
    products,
    setProducts,
    setProductResourcesMap,
    setMinPersons,
    resources,
    setResources,
  } = useBooking();

  const [branches, setBranches] = useState([]);
  const [entertainments, setEntertainments] = useState([]);
  const { getBranches, isLoading: isBranchLoading } = useBranch();
  const {
    getAllEntertainmentsByBranch,
    isLoading: isEntertainmentLoading,
    getProductsByEntertainmentId,
  } = useEntertainment();
  const { getResourcesByEntertainmentId, isLoading: isResourceLoading } = useResource();

  // Each select waits for its own list. A single combined flag greyed out the
  // whole cascade whenever any of the requests was in flight, so the freshly
  // opened wizard looked frozen while it loaded bottom-up. Entertainment and
  // product share one flag because both come from the same hook instance.

  // Determines whether the currently selected entertainment has ordered resources configured
  const hasOrderedResourcesOnSelectedEntertainment = () => {
    if (!selectedEntertainment || !Array.isArray(entertainments)) return false;
    const ent = entertainments.find(e => e._id === selectedEntertainment);
    const list = ent?.orderedResources?.resources;
    return Array.isArray(list) && list.length > 0;
  };

  // Fetch branches on component mount
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await getBranches();
        setBranches(response || []);

        if (branchId) {
          setSelectedBranch(branchId);
        }

        // reset map
        setSelectedProduct(null);
        setSelectedResource(null);
        setProductResourcesMap(new Map());
      } catch (error) {
        if (error.response?.status === 401) {
          setEntertainmentError('Bitte melde dich erneut an');
        } else {
          setEntertainmentError('Ein Fehler ist aufgetreten');
        }
      }
    };

    fetchBranches();
  }, []);

  // Fetch entertainments when branch changes
  useEffect(() => {
    const fetchEntertainments = async () => {
      if (!selectedBranch) {
        setEntertainments([]);
        return;
      }

      try {
        const response = await getAllEntertainmentsByBranch(selectedBranch);
        setEntertainments(response || []);

        if (
          response &&
          response.length > 0 &&
          entertainmentId &&
          response.find(e => e._id === entertainmentId)
        ) {
          setSelectedEntertainment(entertainmentId);
          setSelectedEntertainmentObject(response.find(e => e._id === entertainmentId));
        } else if (response && response.length > 0) {
          // auto select first entertainment if available
          setSelectedEntertainment(response[0]._id);
          setSelectedEntertainmentObject(response[0]);
        } else {
          setSelectedEntertainment(null);
          setSelectedEntertainmentObject(null);
        }
      } catch (error) {
        setEntertainmentError('Ein Fehler ist aufgetreten');
      }
    };

    fetchEntertainments();
  }, [selectedBranch]);

  // Fetch products when entertainment changes
  useEffect(() => {
    const fetchProducts = async () => {
      if (!selectedEntertainment) {
        setProducts([]);
        setSelectedProduct(null);
        return;
      }

      try {
        const response = await getProductsByEntertainmentId(selectedEntertainment);
        const sorted = (response || []).slice().sort((a, b) => {
          const hasA = a.sortPriority != null;
          const hasB = b.sortPriority != null;
          if (hasA && !hasB) return -1;
          if (!hasA && hasB) return 1;
          if (!hasA && !hasB) return 0;
          return b.sortPriority - a.sortPriority;
        });
        setProducts(sorted);

        // Store resource count for each product in the map
        const newProductResourcesMap = new Map();
        const currentEntertainment = entertainments.find(e => e._id === selectedEntertainment);
        const hasOrdered =
          currentEntertainment?.orderedResources?.resources &&
          Array.isArray(currentEntertainment.orderedResources.resources) &&
          currentEntertainment.orderedResources.resources.length > 0;

        if (sorted) {
          sorted.forEach(product => {
            const resourceCount = hasOrdered
              ? 0
              : Array.isArray(product?.resourceIds)
                ? product.resourceIds.length
                : 0;
            newProductResourcesMap.set(product._id, resourceCount);
          });
        }
        setProductResourcesMap(newProductResourcesMap);

        // Keep a product that is still in the list instead of always jumping back
        // to the first one — a cart item re-opened for editing sets its product
        // programmatically and would otherwise lose it here.
        if (sorted && sorted.length > 0) {
          if (!selectedProduct || !sorted.find(p => p._id === selectedProduct)) {
            setSelectedProduct(sorted[0]._id);
          }
        } else {
          setSelectedProduct(null);
        }
      } catch (error) {
        setEntertainmentError('Ein Fehler ist aufgetreten');
      }
    };

    fetchProducts();
  }, [selectedEntertainment, entertainments]);

  // Fetch resources when product changes
  useEffect(() => {
    const fetchResources = async () => {
      if (!selectedProduct || !selectedEntertainment) {
        setResources([]);
        setSelectedResource(null);
        return;
      }

      // When orderedResources are configured on the entertainment, skip resource selection entirely
      if (hasOrderedResourcesOnSelectedEntertainment()) {
        setResources([]);
        setSelectedResource(null);
        return;
      }

      try {
        // Get all resources from entertainment
        const entertainmentResourcesRaw = await getResourcesByEntertainmentId(
          selectedEntertainment,
          false
        );
        const entertainmentResources = Array.isArray(entertainmentResourcesRaw)
          ? entertainmentResourcesRaw
          : entertainmentResourcesRaw?.resources || [];

        // Get the selected product to access its resourceIds
        const selectedProductData = products.find(p => p._id === selectedProduct);
        const productResourceIds = Array.isArray(selectedProductData?.resourceIds)
          ? selectedProductData.resourceIds
          : [];
        const productResourceIdSet = new Set(productResourceIds.map(id => String(id)));

        // Filter entertainment resources by product's resourceIds (intersection)
        const filteredResources = (Array.isArray(entertainmentResources) ? entertainmentResources : [])
          .filter(resource => {
            const rid = resource?._id ?? resource?.id ?? null;
            if (!rid) return false;
            return productResourceIdSet.has(String(rid));
          })
          .map(resource => ({
            ...resource,
            _id: resource?._id ?? resource?.id,
          }));

        setResources(filteredResources || []);
        // Auto-select the first resource, but keep a room that is still available
        // (same reason as for the product above).
        if (filteredResources && filteredResources.length > 0) {
          const keepsResource =
            selectedResource &&
            filteredResources.find(resource => String(resource._id) === String(selectedResource));
          if (!keepsResource) {
            setSelectedResource(String(filteredResources[0]._id));
          }
          setEntertainmentError(null);
        } else {
          setSelectedResource(null);
          // If the product expects resources but none are available, surface a clear error instead of infinite loading.
          if (productResourceIds.length > 0) {
            setEntertainmentError('Für dieses Produkt sind keine passenden Räume verfügbar');
          }
        }
      } catch (error) {
        setEntertainmentError('Ein Fehler ist aufgetreten');
      }
    };

    fetchResources();
  }, [selectedProduct, selectedEntertainment, products, entertainments]);

  const handleBranchChange = event => {
    setSelectedBranch(event.target.value);
    setSelectedEntertainment(null); // Reset entertainment when branch changes
    setSelectedEntertainmentObject(null); // Reset entertainment object when branch changes
    setSelectedProduct(null); // Reset product when branch changes
    setSelectedResource(null); // Reset resource when branch changes
    setEntertainmentError(null);
    setMinPersons(null);
  };

  const handleEntertainmentChange = event => {
    const entId = event.target.value;
    setSelectedEntertainment(entId);
    const entObj = entertainments.find(e => e._id === entId);
    setSelectedEntertainmentObject(entObj || null);
    setSelectedProduct(null); // Reset product when entertainment changes
    setSelectedResource(null); // Reset resource when entertainment changes
    setEntertainmentError(null);
    setMinPersons(null);
  };

  const handleProductChange = event => {
    setSelectedProduct(event.target.value);
    setSelectedResource(null); // Reset resource when product changes
    setEntertainmentError(null);

    const prod = products.find(p => p._id === event.target.value);
    if (prod && prod.minParticipants !== undefined) {
      setMinPersons(prod.minParticipants || null);
    } else {
      setMinPersons(null);
    }
  };

  // If selectedProduct set programmatically (e.g. via props), update minPersons accordingly
  useEffect(() => {
    if (!selectedProduct) return;
    const prod = products.find(p => p._id === selectedProduct);
    if (prod && prod.minParticipants !== undefined) {
      setMinPersons(prod.minParticipants || null);
    }
  }, [selectedProduct, products]);

  const handleResourceChange = event => {
    setSelectedResource(event.target.value);
    setEntertainmentError(null);
  };

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={resources && resources.length > 0 ? 3 : 4}>
          <FormControl fullWidth error={!!entertainmentError}>
            <InputLabel id='branch-select-label'>Filiale</InputLabel>
            <Select
              labelId='branch-select-label'
              value={selectedBranch || ''}
              label='Filiale'
              onChange={handleBranchChange}
              disabled={isBranchLoading || branchLocked}
              color='primary'
            >
              {branches.map(branch => (
                <MenuItem key={branch._id} value={branch._id}>
                  {branch.longName}
                </MenuItem>
              ))}
            </Select>
            {branchLocked && (
              <FormHelperText>
                Warenkorb-Buchungen sind auf eine Filiale beschränkt
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} lg={resources && resources.length > 0 ? 3 : 4}>
          <FormControl fullWidth error={!!entertainmentError}>
            <InputLabel id='entertainment-select-label'>Entertainment</InputLabel>
            <Select
              labelId='entertainment-select-label'
              value={selectedEntertainment || ''}
              label='Entertainment'
              onChange={handleEntertainmentChange}
              disabled={!selectedBranch || isEntertainmentLoading}
              color='primary'
            >
              {entertainments.map(entertainment => (
                <MenuItem key={entertainment._id} value={entertainment._id}>
                  {entertainment.entertainmentName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} lg={resources && resources.length > 0 ? 3 : 4}>
          <FormControl fullWidth error={!!entertainmentError}>
            <InputLabel id='product-select-label'>Produkt</InputLabel>
            <Select
              labelId='product-select-label'
              value={selectedProduct || ''}
              label='Produkt'
              onChange={handleProductChange}
              disabled={!selectedEntertainment || isEntertainmentLoading}
              color='primary'
            >
              {products.map(product => {
                const displayName =
                  product.internalName && product.internalName !== product.name
                    ? `${product.name} (${product.internalName})`
                    : product.name;
                return (
                  <MenuItem key={product._id} value={product._id}>
                    {displayName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
        {resources && resources.length > 0 && (
          <Grid item xs={12} sm={6} lg={3}>
            <FormControl fullWidth error={!!entertainmentError}>
              <InputLabel id='resource-select-label'>Raum</InputLabel>
              <Select
                labelId='resource-select-label'
                value={selectedResource || ''}
                label='Raum'
                onChange={handleResourceChange}
                disabled={!selectedProduct || isResourceLoading}
                color='primary'
              >
                {resources.map(resource => (
                  <MenuItem key={resource._id} value={resource._id}>
                    {resource.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>
      {entertainmentError && (
        <Alert severity='error' sx={{ mt: 2 }}>
          {entertainmentError}
        </Alert>
      )}
    </Box>
  );
}
